import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer/Footer';
import Menu from '../../../components/Menu/Menu';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import DateTime from '../../../components/DateTime/DateTime';
import { getMemoryIndex } from '../../../services/ArgosTraderBotVisionService';
import { getAllAutomations } from '../../../services/AutomationsService';
import { doBacktest } from '../../../services/BacktestsService';
import { getSymbol } from '../../../services/SymbolsService';
import { getPlanLimit } from '../../../utils/planLimits';
import AutomationsList from './AutomationsList';
import './Backtests.css';

const DEFAULT_BACKTEST = {
    symbol: '',
    startBase: 0,
    startQuote: 0,
    startDate: '',
    endDate: '',
    automationIds: [],
};

// Absolute ceiling, mirroring the backend's MAX_BACKTEST_DAYS. The plan cap can
// only tighten it; the candle cap stays server-side because it depends on the
// automations' indexes.
const MAX_BACKTEST_DAYS = 90;

function NewBacktest() {
    const navigate = useNavigate();

    const planDays = getPlanLimit('maxBacktestDays');
    const maxDays =
        planDays > 0
            ? Math.min(planDays, MAX_BACKTEST_DAYS)
            : MAX_BACKTEST_DAYS;

    const [backtest, setBacktest] = useState(DEFAULT_BACKTEST);
    const [automations, setAutomations] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getAllAutomations()
            .then(results => {
                setAutomations(
                    results.filter(
                        a =>
                            !a.schedule &&
                            !a.name.startsWith('GRID ') &&
                            (!backtest.symbol || a.symbol === backtest.symbol),
                    ),
                );
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }, [backtest.symbol]);

    const selectedAutomations = automations.filter(a =>
        backtest.automationIds.includes(a.id),
    );
    const noOrderAutomations = selectedAutomations.filter(
        a => !(a.actions || []).some(action => action.type === 'ORDER'),
    );

    function onInputChange(event) {
        setBacktest(prevState => ({
            ...prevState,
            [event.target.id]: event.target.value,
        }));
    }

    function onSymbolChange(event) {
        if (!event.target.value) return;

        const symbol = event.target.value;
        setBacktest(prevState => ({
            ...DEFAULT_BACKTEST,
            automationIds: prevState.automationIds,
            symbol,
        }));

        getSymbol(symbol)
            .then(symbolObj =>
                Promise.all([
                    getMemoryIndex(symbolObj.base, 'WALLET', null),
                    getMemoryIndex(symbolObj.quote, 'WALLET', null),
                ]),
            )
            .then(([base, quote]) =>
                setBacktest(prevState => ({
                    ...prevState,
                    // the wallet index can be missing (null); fall back to the
                    // documented default instead of sending a null balance
                    startBase: Number.isFinite(parseFloat(base)) ? base : 0,
                    startQuote: Number.isFinite(parseFloat(quote)) ? quote : 0,
                })),
            )
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onSubmit(event) {
        if (isSubmitting) return;

        setError('');

        // DateTime emits a Date object (possibly invalid while incomplete),
        // the service converts it to a timestamp the same way
        const startTime = new Date(backtest.startTime).getTime();
        const endTime = new Date(backtest.endTime).getTime();

        if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
            setError('Fill the start and end dates.');
            return;
        }
        if (startTime >= endTime) {
            setError('The start date must be before the end date.');
            return;
        }
        if (endTime - startTime > maxDays * 24 * 60 * 60 * 1000) {
            setError(`The maximum backtest period is ${maxDays} days.`);
            return;
        }

        const baseNum = parseFloat(backtest.startBase);
        const quoteNum = parseFloat(backtest.startQuote);
        if (
            !Number.isFinite(baseNum) ||
            baseNum < 0 ||
            !Number.isFinite(quoteNum) ||
            quoteNum < 0
        ) {
            setError(
                'Start base and start quote must be non-negative numbers.',
            );
            return;
        }

        setIsSubmitting(true);
        doBacktest(backtest)
            .then(result => {
                navigate('/automations/backtests/' + result.id);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
                setIsSubmitting(false);
            });
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="card card-body shadow border-0 mb-4 mt-4">
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                        <div className="d-block mb-4 mb-md-0">
                            <h2 className="h4">New Backtest</h2>
                        </div>
                    </div>
                    {isSubmitting ? (
                        <div className="alert alert-info d-flex align-items-center mb-3">
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            Creating the backtest...
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="form-group">
                                <label htmlFor="symbol">Symbol:</label>
                                <SelectSymbol
                                    symbol={backtest.symbol}
                                    onChange={onSymbolChange}
                                    onlyFavorites={true}
                                />
                            </div>
                        </div>
                    </div>
                    <AutomationsList
                        automationIds={backtest.automationIds}
                        data={automations}
                        onChange={onInputChange}
                    />
                    {noOrderAutomations.length ? (
                        <div className="alert alert-warning">
                            {noOrderAutomations.map(a => a.name).join(', ')}{' '}
                            {noOrderAutomations.length > 1 ? 'have' : 'has'} no
                            Order action and won't generate orders in the
                            backtest — alerts are not simulated.
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="form-group">
                                <label htmlFor="startBase">Start Base:</label>
                                <input
                                    type="number"
                                    id="startBase"
                                    className="form-control"
                                    placeholder="0"
                                    value={backtest.startBase || ''}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="form-group">
                                <label htmlFor="startQuote">Start Quote:</label>
                                <input
                                    type="number"
                                    id="startQuote"
                                    className="form-control"
                                    placeholder="0"
                                    value={backtest.startQuote || ''}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="form-group">
                                <label htmlFor="startTime">Start Date:</label>
                                <DateTime
                                    id="startTime"
                                    onChange={onInputChange}
                                    date={backtest.startDate || ''}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="form-group">
                                <label htmlFor="endTime">End Date:</label>
                                <DateTime
                                    id="endTime"
                                    onChange={onInputChange}
                                    date={backtest.endDate || ''}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="alert alert-info mt-1">
                        Maximum backtest period: 3 months. On the 1m timeframe,
                        a backtest is also capped at 50,000 candles (~34 days) —
                        pick indexes with a higher interval to cover longer
                        ranges.
                    </div>
                    {error ? (
                        <div className="alert alert-danger mt-1">
                            {typeof error === 'string'
                                ? error
                                : 'Check the form and try again.'}
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="row">
                        <div className="col-12">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onSubmit}
                                disabled={
                                    isSubmitting ||
                                    !backtest.symbol ||
                                    !backtest.automationIds.length
                                }
                            >
                                {isSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Starting...
                                    </>
                                ) : (
                                    'Start Test'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        </>
    );
}

export default NewBacktest;
